import { useInfiniteQuery } from '@tanstack/react-query';

import { fetchOCClaimItems } from '../data-provider/claim-items';
import type { GetOCClaimItemsRequestParams, OCClaimItemsResponse } from '../data-provider/claim-items/api';
import { getOCClaimInfiniteNextPageParam } from '../data-provider/utils/get-next-page';

export interface UseGetOCClaimItemsParams {
  fbOcKey: number | undefined;
  pageSize?: number;
  sortColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
}

type OCClaimItemsPage = OCClaimItemsResponse;

export const useGetOCClaimItems = ({
  fbOcKey,
  pageSize = 10,
  sortColumn = 'itemID',
  sortDirection = 'DESC',
}: UseGetOCClaimItemsParams) => {
  return useInfiniteQuery<OCClaimItemsPage>({
    enabled: Boolean(fbOcKey),
    getNextPageParam: getOCClaimInfiniteNextPageParam,
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) => {
      const params: GetOCClaimItemsRequestParams = {
        fbOcKey: Number(fbOcKey),
        pageIndex: Number(pageParam),
        pageSize,
        sortColumn,
        sortDirection,
      };
      return fetchOCClaimItems(params);
    },
    queryKey: ['claims-over-charge-items', fbOcKey, sortColumn, sortDirection],
    select: (data) => {
      const items = data.pages.flatMap((page) => page?.data ?? []);
      const lastPage = data.pages[data.pages.length - 1];
      const pageIndex = data.pages.length;

      return {
        ...data,
        items,
        pageIndex,
        totalBilledAmount: Number(lastPage?.totalBilledAmount ?? 0),
        totalCorrectCharges: Number(lastPage?.totalCorrectCharges ?? 0),
        totalOverchargeAmount: Number(lastPage?.totalOverchargeAmount ?? 0),
        totalPaidAmount: Number(lastPage?.totalPaidAmount ?? 0),
        totalRecoveryAmount: Number(lastPage?.totalRecoveryAmount ?? 0),
        totalRows: Number(lastPage?.totalRows ?? 0),
      };
    },
  });
};
